import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../../hooks/useAuth';
import { useSpace } from '../../../../hooks/useSpace';
import useConnectDrive from '../../api/useConnectDrive';
import { getGoogleOAuthRedirectUri } from '../../hooks/useStartGoogleOAuth';

type Status = 'idle' | 'processing' | 'success' | 'error';

export function useGoogleDriveCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { memberships, isLoading: spaceLoading } = useSpace();
  const connect = useConnectDrive();

  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasRunRef = useRef(false);

  const code = searchParams.get('code');
  const spaceId = searchParams.get('state');
  const oauthError = searchParams.get('error');

  useEffect(() => {
    // Guard against React 18 StrictMode double-invocation
    if (hasRunRef.current) return;

    // Wait for auth + spaces to resolve before running
    if (authLoading || spaceLoading) return;

    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    if (oauthError) {
      hasRunRef.current = true;
      setStatus('error');
      setErrorMessage(`Google a refusé la connexion : ${oauthError}`);
      return;
    }

    if (!code || !spaceId) {
      hasRunRef.current = true;
      setStatus('error');
      setErrorMessage('Paramètres manquants dans la réponse Google.');
      return;
    }

    hasRunRef.current = true;
    setStatus('processing');

    connect
      .mutateAsync({ code, spaceId, redirectUri: getGoogleOAuthRedirectUri() })
      .then(() => {
        setStatus('success');
        const membership = memberships.find((m) => m.space.id === spaceId);
        const target = membership ? `/spaces/${membership.space.slug}/settings/google-drive` : '/';
        navigate(target, { replace: true });
      })
      .catch((err: Error) => {
        setStatus('error');
        setErrorMessage(err.message || 'Échec de la connexion à Google Drive.');
      });
  }, [
    authLoading,
    spaceLoading,
    user,
    oauthError,
    code,
    spaceId,
    connect,
    memberships,
    navigate,
  ]);

  function handleBackToSettings() {
    if (!spaceId) {
      navigate('/', { replace: true });
      return;
    }
    const membership = memberships.find((m) => m.space.id === spaceId);
    const target = membership ? `/spaces/${membership.space.slug}/settings/google-drive` : '/';
    navigate(target, { replace: true });
  }

  return {
    status,
    errorMessage,
    handleBackToSettings,
  };
}
